"use client";

import { FunctionComponent, PropsWithChildren, ReactNode } from "react";
import {
    Body,
    Main,
    MainInner,
    Sidebar,
    SidebarInner,
    SidebarMain,
    SidebarToggle,
    SidebarToggleBorder,
    SidebarToggleButton,
    SidebarToggleButtonBottom,
    SidebarToggleButtonInnerWrapper,
    SidebarToggleButtonTop,
    SidebarTop,
    Topbar,
    TopbarInner,
    Wrapper,
} from "./layout.styles";
import { Tooltip } from "../../tooltip/tooltip";

export interface LayoutProps {
    /**
     * Whether the sidebar should be expanded or not.
     */
    expanded?: boolean;

    /**
     * The logo will be displayed on the top left hand corner.
     */
    logo?: ReactNode;

    /**
     * Content of the sidebar.
     */
    sidebar?: ReactNode;

    /**
     * Color of the sidebar in hex format.
     */
    theme?: "default" | "light";

    /**
     * Content of the topbar.
     */
    topbar?: ReactNode;

    /**
     * Show toggle sidebar button.
     */
    showToggleSidebarButton?: boolean;

    /**
     * Translations of the layout.
     */
    translation?: {
        sidebarToggleButton: string;
        downsize: string;
        enlarge: string;
    };

    /**
     * A callback function which will be fired on click sidebar toggle button.
     */
    onClickSidebarToggleButton?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const Layout: FunctionComponent<PropsWithChildren<LayoutProps>> = ({
    logo,
    sidebar,
    topbar,
    expanded,
    theme,
    translation,
    showToggleSidebarButton,
    onClickSidebarToggleButton,
    children,
}) => {
    return (
        <Wrapper>
            {sidebar && (
                <Sidebar expanded={expanded} layoutTheme={theme}>
                    <SidebarTop>{logo}</SidebarTop>
                    <SidebarMain>
                        <SidebarInner>{sidebar}</SidebarInner>
                        {showToggleSidebarButton && (
                            <SidebarToggle expanded={expanded}>
                                <SidebarToggleBorder layoutTheme={theme} />
                                <Tooltip
                                    text={expanded ? translation?.downsize || "Downsize" : translation?.enlarge || "Enlarge"}
                                    placement="right-center"
                                    theme="standalone"
                                >
                                    <SidebarToggleButton
                                        tabIndex={1}
                                        role="button"
                                        aria-label={translation?.sidebarToggleButton || "Navigation menu with toggle function"}
                                        aria-expanded={expanded}
                                        onClick={onClickSidebarToggleButton}
                                    >
                                        <SidebarToggleButtonInnerWrapper expanded={expanded}>
                                            <SidebarToggleButtonTop expanded={expanded} />
                                            <SidebarToggleButtonBottom expanded={expanded} />
                                        </SidebarToggleButtonInnerWrapper>
                                    </SidebarToggleButton>
                                </Tooltip>
                            </SidebarToggle>
                        )}
                    </SidebarMain>
                </Sidebar>
            )}
            <Body>
                {topbar && (
                    <Topbar>
                        <TopbarInner>{topbar}</TopbarInner>
                    </Topbar>
                )}
                <Main>
                    <MainInner>{children}</MainInner>
                </Main>
            </Body>
        </Wrapper>
    );
};
